import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Database, Lightning, CheckCircle, WarningCircle, ProhibitInset, ArrowUp, ArrowDown,
  Play, ArrowClockwise, Coins, Stack, Buildings, UsersThree, Briefcase, Detective,
} from "@phosphor-icons/react";

const OPS = [
  { k: "person_search", l: "Recherche décideurs" },
  { k: "company_enrich", l: "Enrichissement société" },
  { k: "email_enrich", l: "Enrichissement email" },
];

const HEALTH = {
  operational: { l: "Opérationnel", c: "#22C55E", Icon: CheckCircle },
  quota_exhausted: { l: "Quota épuisé", c: "#F5A623", Icon: WarningCircle },
  insufficient_credits: { l: "Crédits insuffisants", c: "#F5A623", Icon: WarningCircle },
  rate_limited: { l: "Débit limité", c: "#F97316", Icon: WarningCircle },
  degraded: { l: "Dégradé", c: "#F97316", Icon: WarningCircle },
  disabled: { l: "Désactivé", c: "#8A8A93", Icon: ProhibitInset },
};

function HealthBadge({ status }) {
  const m = HEALTH[status] || HEALTH.operational;
  const { Icon } = m;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
      style={{ color: m.c, background: `${m.c}1a` }} data-testid="provider-health-badge">
      <Icon size={13} weight="fill" /> {m.l}
    </span>
  );
}

export default function AdminProviders() {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState([]);
  const [logs, setLogs] = useState([]);
  const [testing, setTesting] = useState(null);
  const [drafts, setDrafts] = useState({});      // édits locaux (endpoints, coûts, limites)
  const [days] = useState(30);
  const [verify, setVerify] = useState({ operation: "person_search", path: "", label: "", busy: false });
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [bd, setBd] = useState({
    apiKey: "", busy: false,
    tp: { company: "Microsoft", role: "Talent Acquisition", res: null, busy: false },
    tc: { company: "Microsoft", res: null, busy: false },
  });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/providers");
      setData(data);
      const d = {};
      data.providers.forEach((p) => {
        d[p.name] = {
          endpoints: { ...(p.endpoints || {}) },
          cost_per_credit: p.cost_per_credit, cost_per_call: p.cost_per_call,
          max_credits_val: p.max_credits, credit_alert_threshold: p.credit_alert_threshold,
        };
      });
      d.__limits = { ...data.limits };
      setDrafts(d);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chargement impossible"); }
  }, []);
  const loadUsage = useCallback(async () => {
    try { const { data } = await api.get(`/admin/providers/usage?days=${days}`); setUsage(data.providers); }
    catch { /* */ }
  }, [days]);
  const loadLogs = useCallback(async () => {
    try { const { data } = await api.get("/admin/providers/logs?limit=60"); setLogs(data); } catch { /* */ }
  }, []);

  useEffect(() => { load(); loadUsage(); loadLogs(); }, [load, loadUsage, loadLogs]);

  const patch = async (partial, msg) => {
    try {
      await api.put("/admin/providers/config", partial);
      if (msg) toast.success(msg);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de mise à jour"); }
  };

  const setProvider = (name, obj, msg) => patch({ providers: { [name]: obj } }, msg);
  const toggleOp = (name, op, val) => setProvider(name, { ops: { [op]: val } });

  const reorder = (op, idx, dir) => {
    const arr = [...(data.waterfall[op] || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    patch({ waterfall: { [op]: arr } }, "Ordre de priorité mis à jour");
  };

  const testProvider = async (name) => {
    setTesting(name);
    try {
      const { data } = await api.post(`/admin/providers/${name}/test`);
      if (data.ok) toast.success(`${name} : connexion OK${data.credits_consumed ? ` (${data.credits_consumed} crédits)` : ""}`);
      else toast.error(`${name} : ${data.status} — ${data.message || ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test impossible"); }
    finally { setTesting(null); }
  };

  const saveEndpoints = (name) => {
    const d = drafts[name] || {};
    setProvider(name, {
      endpoints: d.endpoints, max_credits: Number(d.max_credits_val) || 15,
      ...(d.cost_per_credit != null ? { cost_per_credit: Number(d.cost_per_credit) } : {}),
      ...(d.cost_per_call != null ? { cost_per_call: Number(d.cost_per_call) } : {}),
      ...(d.credit_alert_threshold != null ? { credit_alert_threshold: Number(d.credit_alert_threshold) } : {}),
    }, "Réglages enregistrés");
  };
  const saveLimits = () => patch({ limits: {
    max_contacts_per_company: Number(drafts.__limits.max_contacts_per_company) || 5,
    max_provider_attempts: Number(drafts.__limits.max_provider_attempts) || 3,
    stop_after_usable: Number(drafts.__limits.stop_after_usable) || 3,
    coldiq_daily_credit_cap: Number(drafts.__limits.coldiq_daily_credit_cap) || 0,
    coldiq_company_credit_cap: Number(drafts.__limits.coldiq_company_credit_cap) || 0,
  } }, "Limites enregistrées");

  const runVerify = async () => {
    if (!verify.path.trim()) { toast.error("Indiquez le chemin de l'endpoint"); return; }
    setVerify((v) => ({ ...v, busy: true }));
    try {
      const { data } = await api.post("/admin/providers/coldiq/verify-endpoint",
        { operation: verify.operation, path: verify.path.trim(), label: verify.label.trim() || undefined });
      if (data.ok) toast.success(`Vérifié ✓ ${data.result_count} résultat(s) — ajouté au menu`);
      else toast.error(`Rejeté : ${data.result_count} résultat (${data.status}) — non proposé`);
      setVerify((v) => ({ ...v, path: "", label: "" }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Vérification impossible"); }
    finally { setVerify((v) => ({ ...v, busy: false })); }
  };

  const sendSummary = async () => {
    setSummaryBusy(true);
    try {
      const { data } = await api.post("/admin/providers/summary/send-now");
      toast.success(`Récap envoyé à ${data.sent || 0} admin(s)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Envoi impossible"); }
    finally { setSummaryBusy(false); }
  };

  // ---- Bright Data : découverte datasets, config, tests ----
  const bdDiscover = async (endpoint) => {
    setBd((s) => ({ ...s, busy: true }));
    try {
      if (bd.apiKey.trim()) await api.post("/admin/providers/brightdata/config", { api_key: bd.apiKey.trim() });
      const { data } = await api.post(`/admin/providers/brightdata/${endpoint}`);
      if (data.ok) {
        toast.success(`Bright Data connecté — ${data.total || data.count || 0} datasets détectés`);
        setBd((s) => ({ ...s, apiKey: "" }));
        load();
      } else {
        toast.error(data.message || "Échec de connexion Bright Data");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test impossible"); }
    finally { setBd((s) => ({ ...s, busy: false })); }
  };
  const bdSave = async (fields, msg) => {
    try { await api.post("/admin/providers/brightdata/config", fields); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec d'enregistrement"); }
  };
  const bdTestPerson = async () => {
    setBd((s) => ({ ...s, tp: { ...s.tp, busy: true, res: null } }));
    try {
      const { data } = await api.post("/admin/providers/brightdata/test-person-search",
        { company: bd.tp.company, role: bd.tp.role });
      setBd((s) => ({ ...s, tp: { ...s.tp, busy: false, res: data } }));
      if (data.ok) toast.success(`Dataset employés opérationnel — ${data.count} profil(s)`);
      else toast.error(`Échec : ${data.message || data.status}`);
    } catch (e) {
      setBd((s) => ({ ...s, tp: { ...s.tp, busy: false } }));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test impossible");
    }
  };
  const bdTestCompany = async () => {
    setBd((s) => ({ ...s, tc: { ...s.tc, busy: true, res: null } }));
    try {
      const { data } = await api.post("/admin/providers/brightdata/test-company-search", { company: bd.tc.company });
      setBd((s) => ({ ...s, tc: { ...s.tc, busy: false, res: data } }));
      if (data.ok) toast.success("Dataset entreprises opérationnel");
      else toast.error(`Échec : ${data.message || data.status}`);
    } catch (e) {
      setBd((s) => ({ ...s, tc: { ...s.tc, busy: false } }));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test impossible");
    }
  };

  if (!data) return <div className="p-8 text-muted" data-testid="providers-loading">Chargement…</div>;

  return (
    <div className="space-y-8" data-testid="admin-providers">
      <div>
        <div className="overline mb-1">Fournisseurs de données · GTM Router</div>
        <h1 className="font-display text-3xl">Fournisseurs de données</h1>
        <p className="text-muted text-sm mt-2 max-w-3xl">
          InterKO reste <strong>agnostique</strong> : le cache InterKO est interrogé en premier, puis un
          waterfall de fournisseurs (Prospeo direct, ColdIQ hub). InterKO garde le contrôle du routage,
          du cache, des priorités, du fallback, des coûts et de la déduplication. Les clés API restent
          strictement côté serveur.
        </p>
      </div>

      {/* Cache InterKO (épinglé) */}
      <div className="surface rounded-lg p-5 border border-accent/30 flex items-center gap-4" data-testid="provider-cache-card">
        <div className="w-11 h-11 rounded-lg bg-accent/15 flex items-center justify-center">
          <Database size={22} className="text-accent" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold">Cache InterKO</span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-accent bg-accent/15">Étape 1 · 0 crédit</span>
          </div>
          <p className="text-muted text-sm mt-0.5">{data.cache.note}</p>
        </div>
      </div>

      {/* Cartes fournisseurs */}
      <div className="grid gap-5 lg:grid-cols-2">
        {data.providers.map((p) => {
          const d = drafts[p.name] || {};
          return (
            <div key={p.name} className="surface rounded-lg p-5 border border-border space-y-4" data-testid={`provider-card-${p.name}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Lightning size={18} className="text-accent" />
                    <span className="font-display text-lg">{p.label}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <HealthBadge status={p.enabled ? p.health.status : "disabled"} />
                    <span className="text-xs text-muted">
                      Clé : {p.key_configured ? <span className="text-emerald-400">configurée</span> : <span className="text-red-400">absente</span>}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <span className="text-muted">Activé</span>
                    <Switch checked={p.enabled} onCheckedChange={(v) => setProvider(p.name, { enabled: v })}
                      data-testid={`provider-toggle-${p.name}`} />
                  </label>
                  <button onClick={() => testProvider(p.name)} disabled={testing === p.name}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-md px-3 py-1.5 hover-lift"
                    data-testid={`provider-test-${p.name}`}>
                    <Play size={13} /> {testing === p.name ? "Test…" : "Tester"}
                  </button>
                </div>
              </div>

              {/* Opérations */}
              <div className="grid grid-cols-3 gap-2">
                {OPS.map((op) => (
                  <label key={op.k} className="flex flex-col gap-1.5 surface-2 rounded-md p-2.5 border border-border/60">
                    <span className="text-xs text-muted leading-tight">{op.l}</span>
                    <Switch checked={!!(p.ops || {})[op.k]} onCheckedChange={(v) => toggleOp(p.name, op.k, v)}
                      data-testid={`provider-op-${p.name}-${op.k}`} />
                  </label>
                ))}
              </div>

              {/* ColdIQ : simulate + endpoints + max credits */}
              {p.name === "coldiq" && (
                <div className="space-y-3 pt-1">
                  <label className="flex items-center justify-between text-sm">
                    <span>Mode simulé <span className="text-muted text-xs">(réponses factices, 0 crédit)</span></span>
                    <Switch checked={p.simulate} onCheckedChange={(v) => setProvider(p.name, { simulate: v },
                      v ? "Mode simulé activé" : "Mode live activé")} data-testid="provider-coldiq-simulate" />
                  </label>
                  <div className="grid gap-2">
                    {OPS.map((op) => {
                      const opts = (p.endpoint_options || {})[op.k] || [];
                      const cur = (d.endpoints || {})[op.k] || "";
                      const known = opts.some((o) => o.value === cur);
                      return (
                        <div key={op.k} className="flex items-center gap-2">
                          <span className="text-xs text-muted w-40 shrink-0">{op.l} · fournisseur</span>
                          <select value={cur} data-testid={`provider-endpoint-${op.k}`}
                            onChange={(e) => setDrafts((s) => ({ ...s, [p.name]: { ...s[p.name], endpoints: { ...s[p.name].endpoints, [op.k]: e.target.value } } }))}
                            className="h-8 text-xs bg-surface-2 border border-border rounded-md px-2 flex-1 min-w-0">
                            {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            {!known && cur && <option value={cur}>⚠ {cur} (déconseillé — ne renvoie rien)</option>}
                          </select>
                        </div>
                      );
                    })}
                    <p className="text-[0.7rem] text-muted">Apollo « People API Search » consomme des crédits sans résultat sur ce compte — choisissez Findymail. Désactivez une opération avec le switch ci-dessus pour couper totalement ColdIQ dessus.</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-xs text-muted">
                      Plafond crédits / appel
                      <Input type="number" className="h-8 w-20 text-xs" value={d.max_credits_val ?? 15}
                        onChange={(e) => setDrafts((s) => ({ ...s, [p.name]: { ...s[p.name], max_credits_val: e.target.value } }))}
                        data-testid="provider-max-credits" />
                    </label>
                    <label className="flex items-center gap-2 text-xs text-muted">
                      € / crédit
                      <Input type="number" step="0.01" className="h-8 w-20 text-xs" value={d.cost_per_credit ?? 0.05}
                        onChange={(e) => setDrafts((s) => ({ ...s, [p.name]: { ...s[p.name], cost_per_credit: e.target.value } }))} />
                    </label>
                    <label className="flex items-center gap-2 text-xs text-muted">
                      Alerte sous
                      <Input type="number" className="h-8 w-24 text-xs" value={d.credit_alert_threshold ?? 200}
                        onChange={(e) => setDrafts((s) => ({ ...s, [p.name]: { ...s[p.name], credit_alert_threshold: e.target.value } }))}
                        data-testid="provider-credit-threshold" />
                    </label>
                  </div>
                  <div className="flex items-center justify-between surface-2 rounded-md px-3 py-2 border border-border/60" data-testid="coldiq-balance">
                    <span className="text-xs text-muted flex items-center gap-1.5"><Coins size={14} /> Crédits restants</span>
                    <span className={`text-sm font-semibold ${p.credits_low ? "text-amber-300" : "text-fg"}`}>
                      {p.credits_remaining != null ? Math.round(p.credits_remaining).toLocaleString("fr-FR") : "—"}
                    </span>
                  </div>
                  {p.credits_low && (
                    <div className="text-xs text-amber-300" data-testid="coldiq-low-credits">
                      ⚠ Solde de crédits ColdIQ faible — pensez à recharger.
                    </div>
                  )}
                  <button onClick={() => saveEndpoints(p.name)}
                    className="text-xs font-semibold text-accent hover:underline" data-testid="provider-save-coldiq">
                    Enregistrer les réglages ColdIQ
                  </button>

                  <div className="pt-2 border-t border-border/50 space-y-2" data-testid="verify-endpoint-form">
                    <div className="text-xs font-semibold text-fg/80">Vérifier un nouvel endpoint avant de le proposer</div>
                    <div className="flex flex-wrap items-center gap-2">
                      <select value={verify.operation} onChange={(e) => setVerify((v) => ({ ...v, operation: e.target.value }))}
                        className="h-8 text-xs bg-surface-2 border border-border rounded-md px-2" data-testid="verify-op">
                        {OPS.map((op) => <option key={op.k} value={op.k}>{op.l}</option>)}
                      </select>
                      <Input value={verify.path} placeholder="/v1/<provider>/..." className="h-8 text-xs font-mono flex-1 min-w-[160px]"
                        onChange={(e) => setVerify((v) => ({ ...v, path: e.target.value }))} data-testid="verify-path" />
                      <Input value={verify.label} placeholder="Libellé (optionnel)" className="h-8 text-xs w-40"
                        onChange={(e) => setVerify((v) => ({ ...v, label: e.target.value }))} data-testid="verify-label" />
                      <button onClick={runVerify} disabled={verify.busy} data-testid="verify-endpoint-btn"
                        className="h-8 px-3 text-xs font-semibold border border-accent/40 text-accent rounded-md hover-lift disabled:opacity-50">
                        {verify.busy ? "Test…" : "Vérifier"}
                      </button>
                    </div>
                    <p className="text-[0.7rem] text-muted">Un appel réel est effectué (quelques crédits). L'endpoint n'apparaît dans les menus que s'il renvoie des résultats.</p>
                    <div className="flex flex-wrap gap-1.5" data-testid="endpoint-registry">
                      {(data.endpoint_registry || []).map((r) => (
                        <span key={r.id} className="text-[0.68rem] px-2 py-0.5 rounded-full border"
                          style={r.status === "verified" ? { color: "#22C55E", borderColor: "#22C55E55" }
                            : r.status === "failed" ? { color: "#F5A623", borderColor: "#F5A62355" }
                              : { color: "#8A8A93", borderColor: "#8A8A9355" }}
                          title={r.note || ""}>
                          {r.status === "verified" ? "✓" : r.status === "failed" ? "⚠" : "•"} {r.path}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {p.name === "prospeo" && (
                <div className="flex items-center gap-2 pt-1">
                  <label className="flex items-center gap-2 text-xs text-muted">
                    <Coins size={14} /> € / appel estimé
                    <Input type="number" step="0.01" className="h-8 w-24 text-xs" value={d.cost_per_call ?? 0.02}
                      onChange={(e) => setDrafts((s) => ({ ...s, [p.name]: { ...s[p.name], cost_per_call: e.target.value } }))}
                      data-testid="provider-prospeo-cost" />
                  </label>
                  <button onClick={() => saveEndpoints(p.name)} className="text-xs font-semibold text-accent hover:underline">
                    Enregistrer
                  </button>
                </div>
              )}

              {p.name === "brightdata" && (() => {
                const dsMap = Object.fromEntries((p.datasets || []).map((x) => [x.id, x]));
                const optsFor = (slot) => (p.candidates?.[slot] || []).map((id) => dsMap[id]).filter(Boolean);
                const label = (x) => `${x.name}${x.size ? ` · ${Number(x.size).toLocaleString("fr-FR")}` : ""}`;
                const empSet = !!p.employee_dataset_id;
                const comSet = !!p.company_dataset_id;
                const Cap = ({ ok, on, l }) => (
                  <span className="text-[0.68rem] px-2 py-0.5 rounded-full border"
                    style={on ? { color: "#22C55E", borderColor: "#22C55E55" }
                      : ok ? { color: "#F5A623", borderColor: "#F5A62355" }
                        : { color: "#8A8A93", borderColor: "#8A8A9355" }}>
                    {l}: {on ? "ACTIF" : ok ? "DISPO" : "NON CONFIG."}
                  </span>
                );
                return (
                  <div className="space-y-3 pt-1" data-testid="brightdata-config">
                    <div className="flex items-end gap-2">
                      <label className="flex-1 min-w-0">
                        <span className="text-xs text-muted">Clé API Bright Data</span>
                        <Input type="password" value={bd.apiKey}
                          placeholder={p.key_configured ? "•••••••••• (configurée)" : "Collez votre clé Bright Data"}
                          onChange={(e) => setBd((s) => ({ ...s, apiKey: e.target.value }))}
                          className="h-8 text-xs mt-1" data-testid="brightdata-apikey" />
                      </label>
                      <button onClick={() => bdDiscover("test-connection")} disabled={bd.busy}
                        data-testid="brightdata-test-connection"
                        className="h-8 px-3 text-xs font-semibold border border-accent/40 text-accent rounded-md hover-lift disabled:opacity-50 whitespace-nowrap">
                        {bd.busy ? "Connexion…" : "Test Connection"}
                      </button>
                    </div>

                    <label className="flex items-center justify-between text-sm">
                      <span>Mode simulé <span className="text-muted text-xs">(0 quota, données factices)</span></span>
                      <Switch checked={p.simulate}
                        onCheckedChange={(v) => bdSave({ simulate: v }, v ? "Mode simulé activé" : "Mode live activé")}
                        data-testid="brightdata-simulate" />
                    </label>

                    <div className="flex flex-wrap gap-1.5" data-testid="brightdata-capabilities">
                      <Cap ok={p.capabilities?.person} on={empSet && p.capabilities?.person && !p.simulate} l="Personnes" />
                      <Cap ok={p.capabilities?.company} on={comSet && p.capabilities?.company && !p.simulate} l="Entreprises" />
                      <Cap ok={p.capabilities?.jobs} on={p.jobs_enabled && !!p.jobs_dataset_id && !p.simulate} l="Jobs" />
                    </div>

                    {(p.datasets || []).length > 0 ? (
                      <div className="grid gap-2">
                        {[
                          { slot: "employee", key: "employee_dataset_id", l: "Dataset Employés / Personnes", Icon: UsersThree },
                          { slot: "company", key: "company_dataset_id", l: "Dataset Entreprises", Icon: Buildings },
                          { slot: "jobs", key: "jobs_dataset_id", l: "Dataset Jobs (optionnel)", Icon: Briefcase },
                        ].map(({ slot, key, l, Icon }) => {
                          const opts = optsFor(slot);
                          const cur = p[key] || "";
                          const known = opts.some((o) => o.id === cur);
                          return (
                            <div key={slot} className="flex items-center gap-2">
                              <span className="text-xs text-muted w-44 shrink-0 flex items-center gap-1.5"><Icon size={14} /> {l}</span>
                              <select value={cur} data-testid={`brightdata-ds-${slot}`}
                                onChange={(e) => bdSave({ [key]: e.target.value }, "Dataset enregistré")}
                                className="h-8 text-xs bg-surface-2 border border-border rounded-md px-2 flex-1 min-w-0">
                                <option value="">— Sélectionner un dataset —</option>
                                {opts.map((o) => <option key={o.id} value={o.id}>{label(o)}</option>)}
                                {!known && cur && <option value={cur}>{dsMap[cur] ? label(dsMap[cur]) : cur}</option>}
                              </select>
                            </div>
                          );
                        })}
                        <div className="flex items-center gap-3">
                          <button onClick={() => bdDiscover("refresh-datasets")} disabled={bd.busy}
                            data-testid="brightdata-refresh"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-md px-3 py-1.5 hover-lift disabled:opacity-50">
                            <ArrowClockwise size={13} /> Rafraîchir les datasets
                          </button>
                          <label className="flex items-center gap-2 text-xs text-muted ml-auto">
                            € / 1000 enreg.
                            <Input type="number" step="0.01" className="h-8 w-20 text-xs" defaultValue={p.cost_per_record ?? 0}
                              onBlur={(e) => bdSave({ cost_per_record: Number(e.target.value) || 0 })} data-testid="brightdata-cost" />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-muted">Cliquez sur <strong>Test Connection</strong> pour détecter automatiquement les datasets accessibles à votre compte — aucun ID à copier-coller.</p>
                    )}

                    <div className="pt-2 border-t border-border/50 space-y-2">
                      <div className="text-xs font-semibold text-fg/80">Tests (appel réel, non enregistré)</div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Input value={bd.tp.company} onChange={(e) => setBd((s) => ({ ...s, tp: { ...s.tp, company: e.target.value } }))}
                          placeholder="Entreprise" className="h-8 text-xs w-32" data-testid="brightdata-tp-company" />
                        <Input value={bd.tp.role} onChange={(e) => setBd((s) => ({ ...s, tp: { ...s.tp, role: e.target.value } }))}
                          placeholder="Rôle" className="h-8 text-xs w-40" data-testid="brightdata-tp-role" />
                        <button onClick={bdTestPerson} disabled={bd.tp.busy} data-testid="brightdata-test-person"
                          className="h-8 px-3 text-xs font-semibold border border-border rounded-md hover-lift disabled:opacity-50 inline-flex items-center gap-1.5">
                          <Detective size={13} /> {bd.tp.busy ? "…" : "Test décideurs"}
                        </button>
                      </div>
                      {bd.tp.res && (
                        <div className="text-[0.7rem]" data-testid="brightdata-tp-result">
                          {bd.tp.res.ok ? <span className="text-emerald-400">✓ {bd.tp.res.count} profil(s) — {(bd.tp.res.sample || []).map((x) => x.name).join(", ")}</span>
                            : <span className="text-amber-300">⚠ {bd.tp.res.status} — {bd.tp.res.message}</span>}
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <Input value={bd.tc.company} onChange={(e) => setBd((s) => ({ ...s, tc: { ...s.tc, company: e.target.value } }))}
                          placeholder="Entreprise" className="h-8 text-xs w-32" data-testid="brightdata-tc-company" />
                        <button onClick={bdTestCompany} disabled={bd.tc.busy} data-testid="brightdata-test-company"
                          className="h-8 px-3 text-xs font-semibold border border-border rounded-md hover-lift disabled:opacity-50 inline-flex items-center gap-1.5">
                          <Buildings size={13} /> {bd.tc.busy ? "…" : "Test société"}
                        </button>
                      </div>
                      {bd.tc.res && (
                        <div className="text-[0.7rem]" data-testid="brightdata-tc-result">
                          {bd.tc.res.ok ? <span className="text-emerald-400">✓ {bd.tc.res.company?.name} — {bd.tc.res.company?.domain || "domaine ?"}</span>
                            : <span className="text-amber-300">⚠ {bd.tc.res.status} — {bd.tc.res.message}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {p.health?.blocked_ops?.length > 0 && (
                <div className="text-xs text-amber-300">
                  Bloqué : {p.health.blocked_ops.map((b) => `${b.operation} (${b.code})`).join(", ")}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Waterfall + limites */}
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="surface rounded-lg p-5 border border-border" data-testid="waterfall-config">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Stack size={18} className="text-accent" />
              <h2 className="font-display text-lg">Ordre de priorité (waterfall)</h2></div>
          </div>
          <label className="flex items-center justify-between text-sm mb-3 surface-2 rounded-md p-2.5 border border-border/60">
            <span>Routage intelligent <span className="text-muted text-xs">(réordonne selon coût/succès observés)</span></span>
            <Switch checked={!!(data.smart_routing || {}).enabled}
              onCheckedChange={(v) => patch({ smart_routing: { enabled: v } }, v ? "Routage intelligent activé" : "Routage intelligent désactivé")}
              data-testid="smart-routing-toggle" />
          </label>
          {(data.smart_routing || {}).enabled && (
            <p className="text-xs text-muted mb-3">L'ordre ci-dessous devient indicatif : InterKO choisit dynamiquement le meilleur fournisseur par segment ({(data.smart_routing || {}).segment_by || "region"}) à partir des stats.</p>
          )}
          <div className="space-y-4">
            {OPS.map((op) => (
              <div key={op.k}>
                <div className="text-xs text-muted mb-1.5">{op.l}</div>
                <div className="flex flex-wrap gap-2">
                  {(data.waterfall[op.k] || []).map((name, idx) => (
                    <div key={name} className="inline-flex items-center gap-2 surface-2 rounded-md px-3 py-1.5 border border-border/60"
                      data-testid={`waterfall-${op.k}-${name}`}>
                      <span className="text-xs font-semibold">{idx + 1}. {name}</span>
                      <button onClick={() => reorder(op.k, idx, -1)} disabled={idx === 0} className="text-muted hover:text-fg disabled:opacity-30">
                        <ArrowUp size={13} /></button>
                      <button onClick={() => reorder(op.k, idx, 1)} disabled={idx === (data.waterfall[op.k].length - 1)} className="text-muted hover:text-fg disabled:opacity-30">
                        <ArrowDown size={13} /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="surface rounded-lg p-5 border border-border" data-testid="limits-config">
          <h2 className="font-display text-lg mb-3">Garde-fous coûts</h2>
          <div className="space-y-3">
            {[
              { k: "max_contacts_per_company", l: "Contacts max / entreprise" },
              { k: "max_provider_attempts", l: "Fournisseurs max par enrichissement" },
              { k: "stop_after_usable", l: "Stop après N contacts utiles" },
              { k: "coldiq_daily_credit_cap", l: "Plafond crédits ColdIQ / jour (0 = illimité)" },
              { k: "coldiq_company_credit_cap", l: "Plafond crédits ColdIQ / entreprise (0 = illimité)" },
            ].map((f) => (
              <label key={f.k} className="flex items-center justify-between text-sm">
                <span className="text-muted">{f.l}</span>
                <Input type="number" className="h-8 w-20 text-xs" value={(drafts.__limits || {})[f.k] ?? ""}
                  onChange={(e) => setDrafts((s) => ({ ...s, __limits: { ...s.__limits, [f.k]: e.target.value } }))}
                  data-testid={`limit-${f.k}`} />
              </label>
            ))}
            <button onClick={saveLimits} className="text-xs font-semibold text-accent hover:underline" data-testid="save-limits">
              Enregistrer les limites
            </button>
          </div>
        </div>
      </div>

      {/* Analytics */}
      <div className="surface rounded-lg p-5 border border-border" data-testid="providers-analytics">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg">Performance ({days} derniers jours)</h2>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-muted" data-testid="daily-summary-toggle-wrap">
              Récap quotidien
              <Switch checked={!!(data.daily_summary || {}).enabled}
                onCheckedChange={(v) => patch({ daily_summary: { enabled: v } }, v ? "Récap quotidien activé" : "Récap quotidien désactivé")}
                data-testid="daily-summary-toggle" />
            </label>
            <button onClick={sendSummary} disabled={summaryBusy} data-testid="send-summary-now"
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-md px-3 py-1.5 hover-lift disabled:opacity-50">
              {summaryBusy ? "Envoi…" : "Envoyer le récap"}
            </button>
            <button onClick={() => { loadUsage(); loadLogs(); }} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-md px-3 py-1.5 hover-lift">
              <ArrowClockwise size={13} /> Actualiser
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted border-b border-border">
                <th className="py-2 pr-3">Fournisseur</th><th className="py-2 px-3">Requêtes</th>
                <th className="py-2 px-3">Succès</th><th className="py-2 px-3">Contacts</th>
                <th className="py-2 px-3">Utiles</th><th className="py-2 px-3">Crédits</th>
                <th className="py-2 px-3">Coût est.</th><th className="py-2 px-3">Coût/utile</th>
                <th className="py-2 px-3">Quota</th><th className="py-2 px-3">Latence</th>
              </tr>
            </thead>
            <tbody data-testid="analytics-rows">
              {usage.length === 0 && <tr><td colSpan={10} className="py-6 text-center text-muted">Aucune donnée d'usage pour le moment.</td></tr>}
              {usage.map((u) => (
                <tr key={u.provider} className="border-b border-border/50" data-testid={`analytics-row-${u.provider}`}>
                  <td className="py-2 pr-3 font-semibold">{u.provider}</td>
                  <td className="py-2 px-3">{u.requests}</td>
                  <td className="py-2 px-3">{u.success_rate}%</td>
                  <td className="py-2 px-3">{u.contacts}</td>
                  <td className="py-2 px-3">{u.usable_contacts}</td>
                  <td className="py-2 px-3">{u.credits}</td>
                  <td className="py-2 px-3">{u.estimated_cost != null ? `${u.estimated_cost} €` : "—"}</td>
                  <td className="py-2 px-3 font-semibold text-accent">{u.cost_per_usable != null ? `${u.cost_per_usable} €` : "—"}</td>
                  <td className="py-2 px-3">{u.quota_failures}</td>
                  <td className="py-2 px-3">{u.avg_latency_ms} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Logs */}
      <div className="surface rounded-lg p-5 border border-border" data-testid="providers-logs">
        <h2 className="font-display text-lg mb-3">Journal des appels</h2>
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-surface">
              <tr className="text-left text-muted border-b border-border">
                <th className="py-2 pr-3">Date</th><th className="py-2 px-3">Fournisseur</th>
                <th className="py-2 px-3">Sous-jacent</th><th className="py-2 px-3">Opération</th>
                <th className="py-2 px-3">Statut</th><th className="py-2 px-3">Résultats</th>
                <th className="py-2 px-3">Crédits</th><th className="py-2 px-3">Latence</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted">Aucun appel enregistré.</td></tr>}
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-border/40">
                  <td className="py-1.5 pr-3 text-muted">{l.at ? new Date(l.at).toLocaleString("fr-FR") : "—"}</td>
                  <td className="py-1.5 px-3 font-semibold">{l.provider}</td>
                  <td className="py-1.5 px-3">{l.underlying_provider || "—"}</td>
                  <td className="py-1.5 px-3">{l.operation}</td>
                  <td className="py-1.5 px-3">
                    <span className={l.status === "success" ? "text-emerald-400" : "text-amber-300"}>{l.status}</span>
                  </td>
                  <td className="py-1.5 px-3">{l.records_returned ?? 0}</td>
                  <td className="py-1.5 px-3">{l.credits_consumed ?? "—"}</td>
                  <td className="py-1.5 px-3">{l.latency_ms ?? 0} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
